import React from "react";

export function WorkGanttView({
  emptyText = "Set a start date or due date on work items to place them on the Gantt view.",
  onOpenRecord = () => {},
  records = [],
  saving = false,
}) {
  const dayMs = 24 * 60 * 60 * 1000;
  const toTimelineDate = (value) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const scheduledRecords = records
    .map((record) => ({
      ...record,
      timelineStart: toTimelineDate(record.startDate) || toTimelineDate(record.due),
      timelineEnd: toTimelineDate(record.due) || toTimelineDate(record.startDate),
    }))
    .filter((record) => record.timelineStart && record.timelineEnd)
    .map((record) => ({
      ...record,
      timelineEnd: record.timelineEnd < record.timelineStart ? record.timelineStart : record.timelineEnd,
    }));
  const earliest = scheduledRecords.length
    ? new Date(Math.min(...scheduledRecords.map((record) => record.timelineStart.getTime()), today.getTime()))
    : today;
  const latest = scheduledRecords.length
    ? new Date(Math.max(...scheduledRecords.map((record) => record.timelineEnd.getTime()), earliest.getTime() + (13 * dayMs)))
    : new Date(today.getTime() + (13 * dayMs));
  const dayCount = Math.min(31, Math.max(14, Math.round((latest.getTime() - earliest.getTime()) / dayMs) + 1));
  const days = Array.from({ length: dayCount }, (_, index) => new Date(earliest.getTime() + (index * dayMs)));
  const timelineColumns = { gridTemplateColumns: `minmax(190px, 1.2fr) repeat(${dayCount}, minmax(34px, 1fr))` };

  return (
    <div className="work-gantt-wrap" aria-label="Work schedule Gantt view">
      <div className="work-gantt-grid" style={timelineColumns}>
        <div className="work-gantt-task-heading">Work item</div>
        {days.map((day) => (
          <time dateTime={day.toISOString().slice(0, 10)} key={day.toISOString()}>
            {day.toLocaleDateString(undefined, { weekday: "narrow" })}
            <small>{day.getDate()}</small>
          </time>
        ))}
        {scheduledRecords.map((record) => {
          const startOffset = Math.max(0, Math.round((record.timelineStart.getTime() - earliest.getTime()) / dayMs));
          const duration = Math.max(1, Math.round((record.timelineEnd.getTime() - record.timelineStart.getTime()) / dayMs) + 1);
          return (
            <React.Fragment key={record.id}>
              <button className="work-gantt-task" disabled={saving} onClick={() => onOpenRecord(record)} type="button">
                <strong>{record.title}</strong>
                <small>{record.owner || "Unassigned"} · {record.status}</small>
              </button>
              <div
                className={`work-gantt-row priority-${(record.priority || "medium").toLowerCase()}`}
                style={{ gridColumn: `2 / span ${dayCount}`, gridTemplateColumns: `repeat(${dayCount}, minmax(34px, 1fr))` }}
              >
                <button
                  aria-label={`Open ${record.title}`}
                  className="work-gantt-bar"
                  disabled={saving}
                  onClick={() => onOpenRecord(record)}
                  style={{ gridColumn: `${startOffset + 1} / span ${Math.min(duration, dayCount - startOffset)}` }}
                  title={`${record.title}: ${record.startDate || record.due || "No date"} to ${record.due || record.startDate || "No date"}`}
                  type="button"
                >
                  <span>{record.title}</span>
                </button>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {!scheduledRecords.length ? <p className="empty-state">{emptyText}</p> : null}
    </div>
  );
}
